/**
 * hooks/usePendingAttachments.ts
 *
 * Returns all completed requests for a client where case_id IS NULL.
 * These are requests waiting to be manually attached to a dossier.
 * Updated Apr 2026 — stale response guard: fetchIdRef ensures only the latest
 *   in-flight request updates state; setPending([]) on error or client change.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface PendingRequest {
  id: string;
  title: string;
  completed_at: string | null;
}

interface UsePendingAttachmentsResult {
  pending: PendingRequest[];
  loading: boolean;
  refresh: () => void;
}

export function usePendingAttachments(clientId: string | undefined): UsePendingAttachmentsResult {
  const [pending, setPending] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    if (!clientId) {
      ++fetchIdRef.current;
      setPending([]);
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setPending([]);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('requests')
        .select('id, title, completed_at')
        .eq('client_id', clientId)
        .eq('status', 'completed')
        .is('case_id', null)
        .order('completed_at', { ascending: false });

      if (fetchId !== fetchIdRef.current) return;
      if (error) throw error;
      setPending(data || []);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('[usePendingAttachments] fetch error:', err);
      setPending([]);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { pending, loading, refresh: fetch };
}
